import { useState } from 'react';
import { Check } from 'lucide-react';

export type ConfirmOption = {
  id: string;
  label: string;
  defaultChecked?: boolean;
  allowFreeform?: boolean;
  /** When set, renders a small heading above this option — start of a new section. */
  sectionHeading?: string;
};
export type ConfirmAction = { label: string; onClick: () => void };
export type ConfirmState = {
  title?: string;
  executiveSummary?: string;
  message?: string;
  options?: ConfirmOption[];
  primaryAction?: ConfirmAction;
  /**
   * Optional secondary action rendered as an outline button below the primary
   * one (or below the close-label button). Use for "side trips" like "Read Now"
   * that don't replace the user's main confirmation.
   */
  secondaryAction?: ConfirmAction;
  actions?: ConfirmAction[];
  closeLabel?: string;
  tall?: boolean;
  onSubmit?: (selected: string[], freeform: Record<string, string>) => void;
  /**
   * Label for the onSubmit button. Defaults to 'Done'. Ignored unless
   * `onSubmit` is set.
   */
  submitLabel?: string;
  /**
   * Called when the user taps the primary acknowledgement button (the one
   * rendered from `closeLabel` when no `primaryAction`/`onSubmit` is set).
   * If unset, the button falls back to `onClose` for backwards compatibility.
   * Use this to differentiate "user confirmed" from "user dismissed via
   * backdrop / X" — the latter only fires `onClose`.
   */
  onConfirm?: () => void;
  onClose: () => void;
};

export function ConfirmContent({ confirm }: { confirm: ConfirmState }) {
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set((confirm.options ?? []).filter((o) => o.defaultChecked).map((o) => o.id)),
  );
  const [freeform, setFreeform] = useState<Record<string, string>>({});

  function toggle(id: string) {
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isTall = !!confirm.tall;

  return (
    <div className={isTall ? 'px-4 pt-3 pb-4 space-y-4' : 'px-4 pt-2 pb-2 space-y-3'}>
      {confirm.title && (
        <p className={isTall
          ? 'text-[20px] font-bold leading-snug text-foreground'
          : 'text-[17px] font-semibold leading-snug text-foreground'}>
          {confirm.title}
        </p>
      )}
      {confirm.executiveSummary && (
        <div className="rounded-xl bg-primary/5 border border-primary/15 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-primary/80 mb-1.5">
            For you
          </p>
          <p className="text-[15px] text-foreground/90 leading-relaxed">
            {confirm.executiveSummary}
          </p>
        </div>
      )}
      {confirm.message && (
        <div className={isTall ? 'space-y-3' : ''}>
          {(isTall ? confirm.message.split(/\n\s*\n/) : [confirm.message]).map((para, i) => (
            <p
              key={i}
              className={isTall
                ? 'text-[15px] text-foreground/85 leading-relaxed'
                : 'text-[15px] text-muted-foreground leading-relaxed'}
            >
              {para}
            </p>
          ))}
        </div>
      )}

      {confirm.options && confirm.options.length > 0 && (
        <ul className="space-y-2 pt-1">
          {confirm.options.map((opt) => {
            const on = checked.has(opt.id);
            return (
              <li key={opt.id} className="space-y-2">
                {opt.sectionHeading && (
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold pt-2">
                    {opt.sectionHeading}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => toggle(opt.id)}
                  className={[
                    'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all',
                    on ? 'border-primary bg-primary/5' : 'border-border bg-card',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'shrink-0 w-5 h-5 rounded flex items-center justify-center border',
                      on ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40',
                    ].join(' ')}
                  >
                    {on && <Check size={13} strokeWidth={3} />}
                  </span>
                  <span className="text-[15px] leading-snug">{opt.label}</span>
                </button>
                {opt.allowFreeform && on && (
                  <textarea
                    value={freeform[opt.id] ?? ''}
                    onChange={(e) => setFreeform((f) => ({ ...f, [opt.id]: e.target.value }))}
                    placeholder="Tell me more…"
                    rows={2}
                    className="w-full rounded-xl border border-border bg-card px-3 py-2 text-[15px] leading-snug resize-none focus:outline-none focus:border-primary/60"
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!isTall && (
        confirm.onSubmit
          ? <ConfirmFooter
              confirm={{
                ...confirm,
                primaryAction: {
                  label: confirm.submitLabel ?? 'Done',
                  onClick: () => confirm.onSubmit?.([...checked], freeform),
                },
              }}
            />
          : <ConfirmFooter confirm={confirm} />
      )}
    </div>
  );
}

// Standardised footer:
// - Exactly ONE primary button per sheet — same style ("Done"-shaped) every
//   time. Either the explicit primaryAction/onSubmit (when the sheet has a
//   commit action like joining a club), or a `closeLabel` fallback for the
//   light "Got it" sheets that just acknowledge.
// - No trailing Close button — the BottomSheet already gives the user a
//   backdrop tap and an X in the title bar to dismiss without committing.
export function ConfirmFooter({ confirm }: { confirm: ConfirmState }) {
  const actions = confirm.actions ?? (confirm.primaryAction ? [confirm.primaryAction] : []);
  const primaryClass = 'w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm active:scale-[0.98] transition-all';
  const secondaryClass = 'w-full flex items-center justify-center h-12 rounded-full border border-border bg-card text-foreground font-semibold text-sm active:scale-[0.98] transition-all';

  const secondary = confirm.secondaryAction;

  if (actions.length === 0) {
    const closeLabel = confirm.closeLabel ?? 'Done';
    const onDone = confirm.onConfirm ?? confirm.onClose;
    return (
      <div className="space-y-2">
        <button type="button" onClick={() => onDone()} className={primaryClass}>
          {closeLabel}
        </button>
        {secondary && (
          <button type="button" onClick={secondary.onClick} className={secondaryClass}>
            {secondary.label}
          </button>
        )}
      </div>
    );
  }

  const primary = actions[0]!;
  return (
    <div className="space-y-2">
      <button type="button" onClick={primary.onClick} className={primaryClass}>
        {primary.label}
      </button>
      {secondary && (
        <button type="button" onClick={secondary.onClick} className={secondaryClass}>
          {secondary.label}
        </button>
      )}
    </div>
  );
}
